import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { MedRecordsFollowUpOutcomeSchema } from '@/services/api/generated/treatmentRecordRequestsService';

interface MROutcomeDetailsProps {
  outcome: MedRecordsFollowUpOutcomeSchema;
}

export const MROutcomeDetails: React.FC<MROutcomeDetailsProps> = ({
  outcome,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasDetails =
    outcome.call_summary ||
    outcome.vnext_observation?.summary ||
    outcome.timeline ||
    outcome.contact_info ||
    outcome.notes ||
    outcome.next_steps;

  if (!hasDetails) {
    return null;
  }

  return (
    <div className="border-t border-gray-100 pt-2">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
      >
        {isExpanded ? (
          <>
            <ChevronUpIcon className="h-3 w-3" />
            Hide details
          </>
        ) : (
          <>
            <ChevronDownIcon className="h-3 w-3" />
            Show details
          </>
        )}
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-3">
          {/* Key Information - shown first with Next Steps at top */}
          <div className="bg-gray-50 border border-gray-200 rounded-md p-2.5">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">
              Key Information
            </h4>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {outcome.vnext_observation?.summary && (
                <>
                  <dt className="text-gray-500 font-medium">Summary</dt>
                  <dd className="text-gray-900 font-medium">
                    {outcome.vnext_observation.summary}
                  </dd>
                </>
              )}
              {outcome.next_steps && (
                <>
                  <dt className="text-gray-500 font-medium">Next Steps</dt>
                  <dd className="text-gray-900 font-medium">
                    {outcome.next_steps}
                  </dd>
                </>
              )}
              {outcome.timeline && (
                <>
                  <dt className="text-gray-500 font-medium">Timeline</dt>
                  <dd className="text-gray-900 font-medium">
                    {outcome.timeline}
                  </dd>
                </>
              )}
              {outcome.contact_info && (
                <>
                  <dt className="text-gray-500 font-medium">Contact</dt>
                  <dd className="text-gray-900">{outcome.contact_info}</dd>
                </>
              )}
              {outcome.payment_amount && (
                <>
                  <dt className="text-gray-500 font-medium">Payment</dt>
                  <dd className="text-gray-900 font-medium">
                    {outcome.payment_amount}
                  </dd>
                </>
              )}
              {outcome.notes && (
                <>
                  <dt className="text-gray-500 font-medium">Notes</dt>
                  <dd className="text-gray-900">{outcome.notes}</dd>
                </>
              )}
            </dl>
          </div>

          {/* Call Summary - shown at bottom */}
          {outcome.call_summary && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-2.5">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Call Summary
              </h4>
              <p className="text-sm text-gray-700 italic">
                &ldquo;{outcome.call_summary}&rdquo;
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
